import axios from 'axios';
import axiosInstance from './index';
import { environment } from '../../environments/environment';
import { ontologyErrorMessage } from './ontologyApi';

/**
 * Policy document ingestion: upload a document, start INGEST_POLICY_DOCUMENT_TO_ONTOLOGY,
 * follow it to the change set it opens, and decide that change set.
 *
 * See docs/policy-document-ontology-ingestion-api-ui.md in agent-orchestration. The control
 * plane takes the org and the reviewer from the JWT; neither is ever sent from here.
 *
 * The decision is the approval: every item it accepts is written to the ontology as
 * approved, attributed to the signed-in user. Nothing is written before it.
 */

export const INGEST_AGENT_CODE = 'INGEST_POLICY_DOCUMENT_TO_ONTOLOGY';

// ─── Types (spec §4) ─────────────────────────────────────────────────────────

export type ChangeSetStatus =
    | 'extracting' | 'proposed' | 'pending_review' | 'resolving'
    | 'applied' | 'partially_applied' | 'rejected' | 'failed';

export type ChangeType = 'add' | 'modify' | 'retire' | 'unchanged';
export type ItemCollection = 'source_document' | 'concept' | 'policy' | 'parameter';
export type ItemDecision = 'pending' | 'accepted' | 'rejected' | 'edited';
export type Severity = 'low' | 'medium' | 'high';

export interface Evidence { pageNumber?: number; quote?: string; verified?: string }
export interface ValidationEntry { severity: 'error' | 'warning'; field?: string; message?: string }
export interface DiffEntry {
    field: string;
    before?: unknown;
    after?: unknown;
    added?: unknown[];
    removed?: unknown[];
    change?: 'added' | 'removed';
}
export interface WriteReport { action?: string; written?: boolean; problems?: unknown[]; reason?: string }

export interface ChangeSetItem {
    itemId: string;
    collection: ItemCollection;
    code: string;
    changeType: ChangeType;
    proposed: Record<string, any> | null;
    incumbent: { code?: string; effectiveFrom?: string; scopeLevel?: string; status?: string } | null;
    diff?: DiffEntry[];
    evidence?: Evidence[];
    confidence?: 'high' | 'medium' | 'low';
    match?: { method?: string; score?: number | null };
    dependsOn?: string[];
    validation?: ValidationEntry[];
    suggestedDecision?: 'accepted' | 'pending';
    decision?: ItemDecision;
    newConcept?: boolean;
    overridesTier?: boolean;
    conflictingValues?: unknown;
    conceptCode?: string;
    statedEffectiveFrom?: string;
    reason?: string;
    // After a decision
    edits?: Record<string, unknown>;
    reviewNote?: string;
    writeReport?: WriteReport;
}

export interface SourceDocumentInfo {
    title?: string;
    versionLabel?: string;
    issuer?: string;
    issuanceDate?: string;
    effectiveDate?: string;
    documentIdentifier?: string;
    docClass?: string;
    summary?: string;
    sourceLink?: string;
    fileName?: string;
}

export interface FieldProvenance { source?: 'caller' | 'document'; confidence?: string; evidence?: Evidence[] }

export interface RuntimeSampleRow {
    customerId?: string;
    sessionId?: string;
    activityCode?: string;
    orgCode?: string;
    severity?: Severity;
    reasons?: string[];
}

export interface ParameterDelta {
    parameterCode?: string;
    conceptCode?: string;
    activityTemplateCode?: string;
    before?: Record<string, any> | null;
    after?: Record<string, any> | null;
    effective?: boolean;
    shadowedBy?: string;
}

export interface Impact {
    computedAt?: string;
    designTime?: {
        activities?: Array<{
            activityTemplateCode?: string;
            activityTemplateName?: string;
            viaConcepts?: string[];
            viaParameters?: string[];
            flows?: string[];
        }>;
        flows?: unknown[];
        operators?: unknown[];
    };
    parameterDeltas?: ParameterDelta[];
    regulatoryFindings?: Array<{ orgId?: string; message?: string; breachesConstraint?: boolean; severity?: string }>;
    runtime?: {
        summary?: { executions?: number; customers?: number; bySeverity?: Partial<Record<Severity, number>> };
        sample?: RuntimeSampleRow[];
        note?: string;
        unavailable?: boolean;
    };
}

export interface FlaggingReport {
    mode?: string;
    minSeverity?: string;
    eligible?: number;
    flaggedReviews?: number;
    flaggedCapsuleRecords?: number;
    flaggedCards?: number;
    errors?: unknown[];
    completedAt?: string;
}

/** A queue row: everything but `items` and the runtime sample. */
export interface ChangeSetRow {
    changeSetId: string;
    orgId?: string | null;
    scopeLevel?: 'organisation' | 'regulatory';
    sourceDocCode?: string;
    contentHash?: string;
    documentUrl?: string;
    sourceDocument?: SourceDocumentInfo;
    status: ChangeSetStatus;
    failureReason?: string;
    noChanges?: boolean;
    summary?: {
        total?: number;
        byChangeType?: Partial<Record<ChangeType, number>>;
        byCollection?: Partial<Record<ItemCollection, number>>;
    };
    impact?: Impact;
    baseline?: { sourceDocCode?: string; versionLabel?: string; effectiveFrom?: string } | null;
    regulatoryApprovers?: string[];
    flowSessionId?: string;
    createdAt?: string;
    reviewRequestedAt?: string;
}

export interface ChangeSet extends ChangeSetRow {
    items?: ChangeSetItem[];
    identification?: {
        fields?: Record<string, FieldProvenance>;
        match?: { method?: string; score?: number | null; matchedTitle?: string };
        possibleRevisionOf?: Array<{ sourceDocCode?: string; title?: string; score?: number }>;
    };
    documentEffectiveFrom?: string;
    uncitedByRevision?: Array<{ collection?: string; code?: string; alsoCitedBy?: string[] }>;
    reviewedBy?: string;
    reviewNotes?: string;
    reviewedAt?: string;
    appliedAt?: string;
    appliedImpact?: Impact;
    flagging?: FlaggingReport | null;
}

export interface DecisionItem {
    itemId: string;
    decision: 'accept' | 'reject' | 'edit';
    edits?: Record<string, unknown>;
    note?: string;
}

export interface DecisionResult {
    status?: string;
    changeSetId?: string;
    resolution?: ChangeSetStatus;
    accepted?: number;
    written?: number;
    flagging?: FlaggingReport | null;
    reviewedBy?: string;
}

/** What the uploads list shows for one upload (resolved by the control plane). */
export type UploadState =
    | 'uploaded' | 'processing' | 'pending_review' | 'no_changes' | 'decided' | 'failed' | 'duplicate';

export interface PolicyUpload {
    uploadId: string;
    fileName?: string;
    sizeBytes?: number;
    sha256?: string;
    uploadedBy?: string;
    status: 'uploaded' | 'submitted' | 'linked' | 'duplicate' | 'failed';
    state: UploadState;
    referenceId?: string | null;
    changeSetId?: string | null;
    duplicateOf?: string | null;
    failureReason?: string | null;
    submittedMetadata?: Record<string, string> | null;
    createdAt?: string;
    submittedAt?: string | null;
    changeSet?: {
        changeSetId: string;
        status: ChangeSetStatus;
        failureReason?: string | null;
        noChanges?: boolean;
        summary?: ChangeSetRow['summary'] | null;
        title?: string | null;
        versionLabel?: string | null;
        reviewRequestedAt?: string | null;
    } | null;
}

export interface IngestionMetadata {
    title?: string;
    versionLabel?: string;
    issuer?: string;
    issuanceDate?: string;
    effectiveDate?: string;
    documentIdentifier?: string;
    docClass?: string;
    summary?: string;
    sourceDocCode?: string;
}

export type UploadOutcome =
    | { kind: 'uploaded'; uploadId: string; fileName: string; sha256: string }
    | { kind: 'duplicate'; duplicateOf: string; changeSetStatus?: ChangeSetStatus; title?: string | null };

// ─── Errors ──────────────────────────────────────────────────────────────────

export class ChangeSetRequestError extends Error {
    readonly status: number | null;

    constructor(message: string, status: number | null = null) {
        super(message);
        this.name = 'ChangeSetRequestError';
        this.status = status;
    }
}

const toRequestError = (error: unknown, fallback: string): ChangeSetRequestError => {
    if (error instanceof ChangeSetRequestError) return error;
    const anyError = error as any;
    const timedOut = anyError?.code === 'ECONNABORTED';
    return new ChangeSetRequestError(
        timedOut ? 'The request timed out.' : ontologyErrorMessage(error, fallback),
        anyError?.response?.status ?? (timedOut ? 504 : null),
    );
};

const isCancel = (error: unknown) => axios.isCancel(error) || (error as any)?.name === 'CanceledError';

async function request<T>(fn: () => Promise<{ data: any }>, pick: (body: any) => T, fallback: string): Promise<T> {
    try {
        const res = await fn();
        return pick(res?.data ?? {});
    } catch (error) {
        if (isCancel(error)) throw error;
        throw toRequestError(error, fallback);
    }
}

// ─── Upload and start ────────────────────────────────────────────────────────

/** The largest PDF the control plane accepts. */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/** A File's bytes as base64, without the data: URL prefix. */
export const readFileAsBase64 = (file: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('The file could not be read.'));
        reader.onload = () => {
            const result = String(reader.result ?? '');
            const comma = result.indexOf(',');
            resolve(comma >= 0 ? result.slice(comma + 1) : result);
        };
        reader.readAsDataURL(file);
    });

/**
 * Store a PDF with the control plane. Identical bytes that already have a live change set
 * come back as a duplicate rather than an error: the engine would skip them silently, so
 * the user is taken to the existing change set instead.
 */
export async function uploadPolicyDocument(file: File): Promise<UploadOutcome> {
    const contentBase64 = await readFileAsBase64(file);
    try {
        const res = await axiosInstance.post('/ontology/ingestions', {
            fileName: file.name,
            mimeType: file.type || 'application/pdf',
            contentBase64,
        });
        const upload = res?.data?.upload ?? {};
        return { kind: 'uploaded', uploadId: upload.uploadId, fileName: upload.fileName, sha256: upload.sha256 };
    } catch (error) {
        const anyError = error as any;
        const body = anyError?.response?.data;
        if (anyError?.response?.status === 409 && body?.duplicateOf) {
            return { kind: 'duplicate', duplicateOf: body.duplicateOf, changeSetStatus: body.changeSetStatus, title: body.title };
        }
        throw toRequestError(error, 'The document could not be uploaded.');
    }
}

/** Only the values the user typed: each one overrides what the document says. */
export const cleanMetadata = (metadata: IngestionMetadata): IngestionMetadata =>
    Object.fromEntries(
        Object.entries(metadata)
            .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
            .filter(([, value]) => typeof value === 'string' && value !== ''),
    ) as IngestionMetadata;

const executeHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

/**
 * Start INGEST_POLICY_DOCUMENT_TO_ONTOLOGY for an upload, through the ordinary flow
 * endpoint. The control plane resolves the upload to its stored file and fixes the scope;
 * the run takes a few minutes to reach review.
 */
export async function startIngestion(uploadId: string, metadata: IngestionMetadata = {}): Promise<{ referenceId: string | null; uploadId: string }> {
    try {
        const res = await axios.post(
            `${environment.controlplaneUrl}/agent/flow/execute`,
            { agent: { code: INGEST_AGENT_CODE }, data: { uploadId, ...cleanMetadata(metadata) } },
            { headers: executeHeaders() },
        );
        // This endpoint can report a failure inside a 2xx body.
        const body = res.data;
        const errorMessage = typeof body?.error === 'string' ? body.error.trim() : '';
        if (errorMessage || Number(body?.status) >= 400) {
            throw new ChangeSetRequestError(errorMessage || 'The document could not be submitted.', Number(body?.status) || null);
        }
        const reply = body?.data ?? {};
        return { referenceId: reply.referenceId ?? null, uploadId: reply.uploadId ?? uploadId };
    } catch (error) {
        throw toRequestError(error, 'The document could not be submitted.');
    }
}

// ─── Reads ───────────────────────────────────────────────────────────────────

export const listUploads = ({ signal }: { signal?: AbortSignal } = {}) =>
    request(
        () => axiosInstance.get('/ontology/ingestions', { signal }),
        (body) => ({
            uploads: (Array.isArray(body.uploads) ? body.uploads : []) as PolicyUpload[],
            changeSetsUnavailable: Boolean(body.changeSetsUnavailable),
        }),
        'Your uploads could not be loaded.',
    );

export const listChangeSets = (
    { status, limit }: { status?: ChangeSetStatus | ''; limit?: number } = {},
    { signal }: { signal?: AbortSignal } = {},
) =>
    request(
        () => axiosInstance.get('/ontology/change-sets', {
            params: { ...(status ? { status } : {}), ...(limit ? { limit } : {}) },
            signal,
        }),
        (body) => (Array.isArray(body.changeSets) ? body.changeSets : []) as ChangeSetRow[],
        'Change sets could not be loaded.',
    );

export const getChangeSet = (changeSetId: string, { signal }: { signal?: AbortSignal } = {}) =>
    request(
        () => axiosInstance.get(`/ontology/change-sets/${encodeURIComponent(changeSetId)}`, { signal }),
        (body) => body.changeSet as ChangeSet,
        'The change set could not be loaded.',
    );

// ─── Writes ──────────────────────────────────────────────────────────────────

export const recomputeImpact = (changeSetId: string) =>
    request(
        () => axiosInstance.post(`/ontology/change-sets/${encodeURIComponent(changeSetId)}/impact`, {}),
        (body) => body.impact as Impact,
        'Impact could not be recomputed.',
    );

/**
 * Submit the reviewer's decision. Synchronous and slow — it writes the accepted items and
 * flags the affected decisions before it returns — and NEVER retried: while it runs the
 * change set is `resolving`, and a second call gets a 409. On a failure, re-read the
 * change set instead.
 */
export const submitDecision = (changeSetId: string, decision: { review_notes: string; items: DecisionItem[] }) =>
    request(
        () => axiosInstance.post(`/ontology/change-sets/${encodeURIComponent(changeSetId)}/decision`, decision),
        (body) => body.decision as DecisionResult,
        'The decision could not be submitted.',
    );
